/**
 * ops 依頼の活動ログ（誰がいつ何を変えたかを残す append-only の履歴）の共有型と整形。書き手
 * （product-api の各ミューテーション）と読み手（product-innovation-lab の詳細ページ）が、同じ shape・
 * 同じ本文表記・同じ時刻表現を参照するための単一 source。人が書く調査メモ（notes）とは別物で、
 * こちらはシステムが残す変更履歴。
 *
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops 依頼の活動ログ 1 件の共有型と、変更内容の本文（ラベル: 変更前 → 変更後）・JST 表示時刻の整形を全 consumer に提供する。時刻は ISO の at を単一 source とし、at を持たない旧エントリは保存済みの UTC 表記へフォールバックする
 * @graph-connects none
 */
import { JST_OFFSET_MS } from './jst.js';
/** 値が未設定であることを活動ログ本文で示すプレースホルダ（`担当: — → Ray`）。 @graph-connects none */
export const OPS_ACTIVITY_UNSET_LABEL = '—';
/**
 * 活動ログ 1 件を組み立てる。`at`（ISO・表示の単一 source）と後方互換の `time` を同じ時刻から
 * 導出するので、2 つのフィールドがずれることがない。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops 活動ログの 1 エントリを組み立てる。ISO の at と後方互換の time を同一時刻から導出し、両者の食い違いを構造的に防ぐ
 * @graph-connects none
 */
export function opsActivityEntry(actor, text, now = new Date()) {
    const iso = now.toISOString();
    return { time: iso.slice(5, 16), at: iso, actor, text };
}
/** 2 桁ゼロ埋め。 @graph-connects none */
function pad2(value) {
    return String(value).padStart(2, '0');
}
/**
 * 活動ログの表示時刻を JST の `MM-DD HH:mm` で返す。`at` が無い旧エントリ（= `at` 導入前に積まれた
 * 分類変更）と、`at` が壊れているエントリは、保存済みの UTC 表記をそのまま出す（推測で 9 時間ずらすと
 * 誤った時刻を事実として見せてしまう）。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops 活動ログの表示時刻を JST の MM-DD HH:mm へ整形する。at を持たない / 壊れている旧エントリは保存済みの UTC 表記へフォールバックし、誤った JST 時刻を作らない
 * @graph-connects none
 */
export function opsActivityTimeLabel(entry) {
    const fallback = entry.time.replace('T', ' ');
    if (entry.at === undefined)
        return fallback;
    const at = new Date(entry.at);
    if (Number.isNaN(at.getTime()))
        return fallback;
    const jst = new Date(at.getTime() + JST_OFFSET_MS);
    return `${pad2(jst.getUTCMonth() + 1)}-${pad2(jst.getUTCDate())} ${pad2(jst.getUTCHours())}:${pad2(jst.getUTCMinutes())}`;
}
/**
 * 変更 1 件を活動ログ本文の共通表記（`ラベル: 変更前 → 変更後`）へ整形する。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops 活動ログの変更本文を「ラベル: 変更前 → 変更後」の共通表記へ整形し、操作ごとに文言がばらつくのを防ぐ
 * @graph-connects none
 */
export function opsActivityChangeText(label, before, after) {
    return `${label}: ${before} → ${after}`;
}
//# sourceMappingURL=activity.js.map